import React from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

import PageWrapper from '../atoms/PageWrapper';
import { DesignSystemProvider } from '~lib/frontend/design-system';
import { Margin } from '~lib/frontend/design-system/components/atoms/utils';
import Icon from '~lib/frontend/design-system/components/Icon';
import Typography from '~lib/frontend/design-system/components/Typography';
import useContentAsset from '~lib/frontend/hooks/use-content-asset';
import useMobileDetect from '~lib/frontend/hooks/use-mobile-detect';
import AppStoreButtonsSegment from '~website/components/atoms/AppStoreButtonsSegment';
import ContinueInAppLink from '~website/components/atoms/ContinueInAppLink';
import { QRCode } from '~website/components/atoms/QRCode';
import { SubSteps } from '~website/components/atoms/SubSteps';
import TrustSegment from '~website/components/atoms/TrustSegment';
import Footer from '~website/components/layout/footer';
import VibrantScreen from '~website/components/VibrantScreen';
import VibrantScreenCard from '~website/components/VibrantScreen/VibrantScreenCard';
import { trackQuoteEvent } from '~website/features/analytics/store/actions';

const DownloadAppSimple: React.FunctionComponent = () => {
	const dispatch = useDispatch();
	const { isMobile } = useMobileDetect();
	const downloadLogo = useContentAsset('design_system', 'illustrations/il_hands_with_phone.svg');

	return (
		<PageWrapper>
			<VibrantScreen>
				<VibrantScreenCard maxWidth={440}>
					<ContentWrapper>
						{isMobile && (
							<Margin $marginBottom={'extraLarge'}>
								<DownloadLogo src={downloadLogo} />{' '}
							</Margin>
						)}
						<Typography
							$type={'Heading.Medium'}
							$color={'textOnSurfaceBackground'}
							$align={'center'}
							$marginBottom={'large'}
						>
							{'Download Cuvva'}
						</Typography>

						{isMobile && (
							<Typography $type={'Body.Medium'} $color={'textOnSurfaceBackgroundMuted'} $align={'center'}>
								{'Click below to install Cuvva'}
							</Typography>
						)}

						{!isMobile && (
							<>
								<Typography
									$type={'Body.Medium'}
									$color={'textOnSurfaceBackgroundMuted'}
									$align={'center'}
								>
									{'Scan the QR code with your phone camera to install Cuvva.'}
								</Typography>
								<Margin $marginTop={'extraLarge'}>
									<QRCode />
								</Margin>
							</>
						)}

						<SubSteps $color={'primaryFill'} $style={'outline'}>
							<li>
								<Typography $type={'Body.Medium'} $bold $color={'textOnSurfaceBackground'}>
									{'Download Cuvva'}
								</Typography>
							</li>
							<li>
								<Typography $type={'Body.Medium'} $bold $color={'textOnSurfaceBackground'}>
									{'Create an account'}
								</Typography>
							</li>
							<li>
								<Typography $type={'Body.Medium'} $bold $color={'textOnSurfaceBackground'}>
									{'Get covered 🎉'}
								</Typography>
							</li>
						</SubSteps>
					</ContentWrapper>
					{isMobile && (
						<Margin $marginBottom={'extraLarge'}>
							<ContinueInAppLinkWrapper
								onClick={() => dispatch(trackQuoteEvent({ action: 'continue_in_app_button_selected' }))}
							>
								<ContinueInAppLink style={'button'} />
							</ContinueInAppLinkWrapper>
						</Margin>
					)}
					<AppStoreButtonsWrapper
						onClick={() => dispatch(trackQuoteEvent({ action: 'download_cuvva_app_selected' }))}
					>
						<AppStoreButtonsSegment align={'center'} />
					</AppStoreButtonsWrapper>
				</VibrantScreenCard>

				<Margin $marginTop={'large'} $marginBottom={'large'}>
					<DesignSystemProvider themeKey={'dark'} typographyStyle={'expressive'}>
						<TrustSegment trustProvider={'trustpilot'} />
					</DesignSystemProvider>

					<LegalDisclaimerBlock>
						<Icon icon={'ic_check_circle'} $size={'24px'} $color={'textOnFill'} $marginRight={'small'} />
						<Typography $type={'Label.Small'} $color={'textOnFill'}>
							{'FCA Authorised and regulated'}
						</Typography>
					</LegalDisclaimerBlock>
				</Margin>
			</VibrantScreen>

			<Footer landingPage />
		</PageWrapper>
	);
};

const ContentWrapper = styled.div`
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
`;

const DownloadLogo = styled.img`
	width: 128px;
	height: 128px;
`;

const ContinueInAppLinkWrapper = styled.div`
	width: 100%;
`;

const AppStoreButtonsWrapper = styled.div`
	display: flex;
	justify-content: center;
`;

const LegalDisclaimerBlock = styled.div`
	display: flex;
	flex-direction: row;
	justify-content: center;
	align-items: center;
`;

export default DownloadAppSimple;
